import { Folder } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { ROOT_FOLDER_ID } from '@/utils/folders';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from './useFetchFolderContents';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from './useFolderHierarchy';
import { GET_FOLDER_QUERY_KEY } from './useGetFolder';
import { GET_ASSET_QUERY_KEY } from '../assets/useGetAsset';

export const useMoveFolder = (options?: {
  onSuccess?: (movedFolder: Folder) => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationKey: ['moveFolder'],
    mutationFn: ({ folder, parentFolderId }: { folder: Folder; parentFolderId: string | undefined }) =>
      api.folders.moveFolder(tenantId, folder.id, {
        parentFolderId: parentFolderId ?? ROOT_FOLDER_ID,
      }),
    onSuccess: data => {
      options?.onSuccess?.(data.data.folder);
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.removeQueries({
        queryKey: [GET_FOLDER_QUERY_KEY],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [GET_ASSET_QUERY_KEY],
        exact: false,
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      options?.onError?.(error.response?.data.message ?? 'Failed to move folder');
    },
  });

  return {
    moveFolder: mutate,
    isMoving: isPending,
    error,
  };
};
